import React, { useState } from 'react';
import cx from 'classnames';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import { makeStyles } from '@material-ui/core/styles';
import { toUserTimezone } from 'utils';
import ExerciseListItem from './ExerciseListItem';
import ExerciseListItemMeeting from './ExerciseListItemMeeting';
import ScheduleMeeting from './ScheduleMeeting';
import useMeeting from '../useMeeting';
import { ROLES } from 'constant/userTypes';
import Button from 'components/common/Button';
import EditIcon from '@material-ui/icons/Edit';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import Spin from 'components/common/Spin';
import {
  IconButton,
  Divider
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useApolloClient } from 'react-apollo';
import { FETCH_SUMMARY, FETCH_USER_BY_ID } from './queries';

const STATUS_LABEL = {
  inprogress: 'In progress',
  done: 'Completed!',
  get(status) {
    return STATUS_LABEL[status] || 'Not started';
  },
};

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(4),
    boxShadow: '24px 24px 40px -20px rgba(0, 0, 0, 0.07)',
  },
  cardContent: {
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    }
  },
  statusChip: {
    borderRadius: 6,
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(1),
      marginBottom: 0,
    },
  },
  done: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.main,
  },
  inprogress: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.main,
  },
  soonChip: {
    'color': theme.palette.error.main,
    'backgroundColor': theme.palette.error.light,
    '& .MuiChip-icon': {
      fontSize: 16,
      color: theme.palette.primary.main,
    },
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    width: '80%',
    height: '70%',
  },
  container: {
    paddingLeft: '30px',
    paddingRight: '30px',
    overflowY: 'scroll',
    height: '88%',
  },
  close: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  schedule_time: {
    fontSize: '14px',
    marginTop: '-16px'
  }
}));

function hasIncomplete(idx, all) {
  let r = false;
  for (let i = 0; i < idx; i++) {
    const x = all[i];
    if (!(x.status === 'done' || x.status === 'noshow')) {
      r = true;
      break;
    }
  }
  return r;
}

const MilestoneCard = ({ milestone, student, user, onClick, currentIndex }) => {
  const { exercises, name, due_date, status, updated_at } = milestone;
  const classes = useStyles({ status });
  const { onCancelMeeting, reschedule } = useMeeting(student);
  const [showScheduleModal, setSHowScheduleModal] = useState({
    show: false,
    milestoneExercise: null,
    type: null,
    milestoneId: milestone.id,
    milestoneStatus: status
  });
  const canEdit = user.type !== ROLES.PARENT && user.type !== ROLES.STUDENT;
  const [modalOpen, setModalOpen] = useState(false);

  const client = useApolloClient();
  const [meetingData, setMeetingData] = useState(null);
  const [meetingAttendees, setMeetingAttendees] = useState([]);
  const [meetingLoading, setMeetingLoading] = useState(false);

  const milestoneDueDays = due_date
    ? toUserTimezone(due_date, user.timezone).diff(
      toUserTimezone(new Date(), user.timezone),
      'd',
    )
    : -1;

  const dueSoon = milestoneDueDays <= 7;

  const renderDueLabel = () => {
    if (status === 'done') {
      return `on ${toUserTimezone(updated_at, user.timezone).format('LL')}`;
    } else if (due_date && !dueSoon) {
      return `Due ${toUserTimezone(due_date, user.timezone).format('LL')}`;
    }
    return null;
  };

  const onRequestCancel = (task) => {
    return onCancelMeeting(task);
  };

  const handleModalOpen = (exc_id, name) => {
    setModalOpen(true);
    setMeetingLoading(true);
    setMeetingData(null);
    setMeetingAttendees([]);
    client
      .query({
        query: FETCH_SUMMARY,
        variables: {
          id: exc_id,
        },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        setMeetingData({ ...data.student_milestone_exercises[0], name })
        data.student_milestone_exercises[0].meeting_attendees.map((user_id) => {
          client
            .query({
              query: FETCH_USER_BY_ID,
              variables: {
                id: user_id,
              },
              fetchPolicy: 'network-only',
            })
            .then(({ data: user_data }) => {
              setMeetingAttendees(prev => [...prev, user_data.users[0]]);
            })
            .catch(e => {
              console.log(e.message)
            })
        })
      })
      .catch(e => {
        console.log(e.message)
      })
      .finally(() => {
        setMeetingLoading(false);
      })
  }

  const handleModalClose = () => {
    setModalOpen(false);
    setMeetingData(null);
    setMeetingAttendees([]);
  };

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Box
          display="flex"
          marginBottom={{ xs: 2, sm: 4 }}
          flexDirection={{ xs: 'column-reverse', sm: 'row' }}
        >
          <Box flex={1}>
            <Typography gutterBottom variant="h2">
              {name}
            </Typography>
            {canEdit && <Button
              color="primary"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => onClick(milestone)}
            >
              Edit
            </Button>}
          </Box>
          <Box
            display="flex"
            flexDirection={{ xs: 'row', md: 'column' }}
            justifyContent={{ xs: 'space-between' }}
            alignItems="center"
            marginBottom={{ xs: 1 }}
          >
            <Chip
              className={cx(classes.statusChip, classes[status])}
              label={STATUS_LABEL.get(status)}
            />
            {due_date && dueSoon && status !== 'done' && (
              <Chip
                color="primary"
                className={cx(classes.statusChip, classes.soonChip)}
                icon={dueSoon && <ErrorOutlineIcon fontSize="small" />}
                label={
                  <>
                    {milestoneDueDays === 1 && 'due in 1 day'}
                    {milestoneDueDays > 1 &&
                      dueSoon &&
                      `due in ${milestoneDueDays} days`}
                    {milestoneDueDays < 0 && 'past due'}
                    {milestoneDueDays === 0 && 'due today'}
                  </>
                }
              />
            )}
            <Typography
              variant="caption"
              component="div"
              style={{ opacity: 0.6 }}
            >
              {renderDueLabel()}
            </Typography>
          </Box>
        </Box>
        <Typography gutterBottom variant="subtitle2" color="textSecondary">
          Exercises
        </Typography>
        {exercises &&
          exercises.map((studentExercise, idx) => {
            if (studentExercise.exercise.type === 'meeting') {
              return (
                <ExerciseListItemMeeting
                  modalOpen={modalOpen}
                  setModalOpen={setModalOpen}
                  handleModalOpen={handleModalOpen}
                  key={studentExercise.id}
                  studentExercise={studentExercise}
                  exercise={studentExercise.exercise}
                  module={studentExercise.exercise.module}
                  user={user}
                  student={student}
                  // isDisabled={hasIncomplete(idx, exercises)}
                  onRequestSchedule={() =>
                    setSHowScheduleModal({
                      show: true,
                      type: studentExercise.exercise.meeting_type,
                      milestoneExercise: studentExercise,
                      milestoneId: milestone.id,
                      milestoneStatus: status
                    })
                  }
                  onRequestReschedule={() =>
                    setSHowScheduleModal({
                      show: true,
                      reschedule: true,
                      type: studentExercise.exercise.meeting_type,
                      milestoneExercise: studentExercise,
                      milestoneId: milestone.id,
                      milestoneStatus: status
                    })
                  }
                  onRequestCancel={onRequestCancel}
                />
              );
            }
            return (
              <ExerciseListItem
                key={studentExercise.id}
                studentExercise={studentExercise}
                exercise={studentExercise.exercise}
                module={studentExercise.exercise.module}
                user={user}
                currentIndex={currentIndex}
                student={student}
              />
            );
          })}
      </CardContent>
      {showScheduleModal && showScheduleModal.show && (
        <ScheduleMeeting
          onClose={() => setSHowScheduleModal(false)}
          milestoneExercise={showScheduleModal.milestoneExercise}
          reschedule={showScheduleModal.reschedule}
          type={showScheduleModal.type}
          milestoneId={showScheduleModal.milestoneId}
          milestoneStatus={showScheduleModal.milestoneStatus}
          lc_id={student?.profile?.lc_id || student?.lc_id}
          fa_id={student?.profile?.fa_id || student?.fa_id}
          hc_id={student?.profile?.hc_id || student?.hc_id}
          studentEmail={student.email}
          studentName={student.fname + ' ' + student.lname}
          studentId={student.user_id}
          student={student}
        />
      )}

      <>
        <Modal
          aria-labelledby="transition-modal-title"
          aria-describedby="transition-modal-description"
          className={classes.modal}
          open={modalOpen}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 200,
          }}
          onClose={handleModalClose}
        >
          <Fade in={modalOpen}>
            <Box className={classes.paper}>

              <Box className={classes.close}>
                <IconButton onClick={handleModalClose}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <Box className={classes.container}>
                {meetingLoading && <Spin spinning linear />}
                {!meetingLoading && <>
                  <Box flex={1} mb={2}>
                    <h3>{meetingData?.name} - Completed on{' '}
                      {meetingData?.completed_at === null ? toUserTimezone(meetingData?.due_date).format('MMMM D, YYYY') :
                        toUserTimezone(meetingData?.completed_at).format('MMMM D, YYYY')}</h3>



                    <p className={classes.schedule_time}>{meetingData?.due_date &&
                      `Scheduled at ${toUserTimezone(meetingData?.due_date, user.timezone).format(
                        'MM-DD-YY • hh:mm z',
                      )}`}</p>
                  </Box>
                  <Divider />
                  <Box mb={2}>
                    <h3>Meeting Summary</h3>
                    <p>{meetingData?.meeting_summary}</p>
                  </Box>
                  <Box mb={2}>
                    <h3>Attendees</h3>
                    {
                      meetingAttendees?.map((user, index) => {
                        return (
                          <p>{(index + 1) + ". " + user?.user_first_name + " " + user?.user_last_name + " " + (user?.user_type === "parent" ? "(Parent)" : user?.user_type === "student" ? "(Student)" : user?.user_type === "fa_coach" ? "(FC)" : user?.user_type === "head_coach" ? "(HC)" : user?.user_type === "lead_coach" ? "(CC)" : user?.user_type === "admin" ? "(Program Supervisor)" : "")}</p>
                        )
                      })
                    }
                  </Box>
                </>}
              </Box>

            </Box>
          </Fade>
        </Modal>
      </>
    </Card>
  );
};

export default MilestoneCard;
